import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ROUTES } from '../Routes/routes';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../style/homepage.css';
import { Nav } from '../layouts/header';
import { ToothZapFooter } from '../layouts/footer';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const trustItems = [
  'Used by 100+ clinics in New Delhi',
  'Every crown warranted',
  'Verified milling network',
  'In-house CAD design',
];

const problemCards = [
  {
    tag: 'BEFORE',
    copy: 'Clinics either delay treatment with a temporary crown or invest heavily in in-house production that sits idle outside peak hours.',
  },
  {
    tag: 'COST',
    copy: 'Second visits create drop-off, extra chair time, and avoidable coordination overhead between dentist, lab, and patient.',
  },
  {
    tag: 'SOLUTION',
    copy: 'ToothZap links Delhi clinics to in-house CAD design and a verified milling network so eligible crowns are produced and delivered in one visit.',
  },
];

const howSteps = [
  {
    num: '01',
    title: 'Upload & Choose Timeline',
    desc: 'Dentist uploads scan and chooses delivery option (90 minutes or 1–2 days)',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
  {
    num: '02',
    title: 'Quality Manufacturing',
    desc: 'Partner lab mills the crown with quality checks and warranty card',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
      </svg>
    ),
  },
  {
    num: '03',
    title: 'Fast Delivery & Treatment',
    desc: 'Crown delivered to clinic → Patient treated in a single sitting or as scheduled',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <rect x="1" y="3" width="15" height="13" />
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
  },
];

const audiencePanels = [
  {
    tag: 'FOR DENTISTS',
    heading: 'Add same-day crown capability without changing how your clinic sells care.',
    points: [
      'Offer same-day crowns without running an in-house mill',
      'Route scan files into a faster CAD and production workflow',
      'Reduce second-visit friction for eligible restorative cases',
      'Expand capacity with verified local production partners',
    ],
    cta: 'Join Us',
    ctaType: 'primary' as const,
    role: 'Dentist',
  },
  {
    tag: 'FOR LABS',
    heading: 'Become the local milling hub behind rapid same-day restorative delivery.',
    points: [
      'Receive routed digital cases from active clinic demand',
      'Operate as a local production hub with predictable turnaround windows',
      'Increase machine utilization with network-managed case flow',
      'Support same-day delivery commitments across Delhi zones',
    ],
    cta: 'Partner as Hub',
    ctaType: 'link' as const,
    role: 'Milling Hub',
  },
];

const faqs = [
  {
    q: 'Which cases are eligible for 90-minute delivery?',
    a: 'Single-unit anterior and posterior crowns where a digital scan is available at chairside. Complex multi-unit cases may require the scheduled 1–2 day track.',
  },
  {
    q: 'What does the warranty card cover?',
    a: 'Every crown comes with a quality-assurance warranty covering material defects and fit issues for a defined period post-fitment.',
  },
  {
    q: 'How do I join as a dentist?',
    a: "Click 'Join Us' and fill out the onboarding form. Our team will schedule a workflow demo within 24 hours.",
  },
  {
    q: 'Can my existing lab join the milling network?',
    a: 'Yes. Labs with 5-axis milling capacity in Delhi NCR can apply as a production hub. We handle case routing, materials, and delivery logistics.',
  },
];





const Hero: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="tz-section bg-white">
      <div className="container tz-container py-5">
        <div className="row align-items-center g-5 py-4">
        <div className="col-12 col-lg-6">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ duration: 0.8 }}>
              <p className="tz-eyebrow">
                Same-Day Dental Crowns in New Delhi — ToothZap
              </p>
              <h1 className="tz-hero-title">
                The crown is ready
                <br />
                before your patient
                <br />
                leaves the chair.
              </h1>
              <p className="tz-body">
                ToothZap coordinates in-house CAD, verified local milling, and rapid last-mile delivery so eligible restorative cases move from scan to fitment in about 90 minutes.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(ROUTES.REGISTER, { state: { role: 'Dentist' } })}
                  className="btn tz-btn-primary-lg"
                >
                  Join Us
                </button>
                <button
                  onClick={() => navigate(ROUTES.REGISTER)}
                  className="btn tz-btn-outline-lg"
                >
                  Book a Demo
                </button>
              </div>
            </motion.div>
          </div>

          <div className="col-lg-6">
            <video
              className="tz-hero-video"
              src="/toothzap-hero.mp4"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBar: React.FC = () => (
  <div className="tz-trustbar">
    <div className="container tz-container py-2">
      <div className="d-flex flex-wrap align-items-center gap-2 py-1">
        {trustItems.map((item, i) => (
          <React.Fragment key={item}>
            {i > 0 && <span className="tz-dot">·</span>}
            <span className="tz-trust-text">{item}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

const ProblemSection: React.FC = () => (
  <section className="tz-section bg-white">
    <div className="container tz-container py-5">
      <div className="row g-5 align-items-start py-4">
        <div className="col-lg-5">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <p className="tz-eyebrow">The Problem</p>
            <h2 className="tz-section-title">
              Most clinics in Delhi can scan a case quickly, but the real delay happens after the chairside work. ToothZap compresses the downstream path.
            </h2>
            <p className="tz-muted">
              The bottleneck usually appears in design coordination, milling turnaround, and delivery handoff rather than in the scan itself.
            </p>
          </motion.div>
        </div>

        <div className="col-lg-7">
          <div className="row g-3">
            {problemCards.map((card, i) => (
              <div className="col-md-4" key={card.tag}>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  transition={{ delay: i * 0.15, duration: 0.7 }}
                  viewport={{ once: true }}
                  className="tz-card"
                >
                  <p className="tz-card-tag">{card.tag}</p>
                  <p className="tz-card-copy">{card.copy}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks: React.FC = () => (
  <section className="tz-section tz-dark-section">
    <div className="container tz-container py-5">
      <div className="py-4">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="tz-dark-title"
        >
          How a same-day dental crown works in Delhi
        </motion.h2>
        <div className="row g-4">
          {howSteps.map((step, i) => (
            <div className="col-md-4" key={step.num}>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                transition={{ delay: i * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="tz-step-card"
              >
                <div className="tz-step-icon">{step.icon}</div>
                <p className="tz-step-num">{step.num}</p>
                <h3 className="tz-step-title">{step.title}</h3>
                <p className="tz-step-desc">{step.desc}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const AudiencePanels: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="tz-section bg-white">
      <div className="container tz-container py-5">
        <div className="row g-4 py-4">
          {audiencePanels.map((panel, i) => (
            <div className="col-md-6" key={panel.tag}>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                transition={{ delay: i * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className={`tz-audience-card ${i === 0 ? 'tz-audience-blue' : ''}`}
              >
                <p className="tz-eyebrow">{panel.tag}</p>
                <h3 className="tz-audience-title">{panel.heading}</h3>
                <ul className="list-unstyled mb-4">
                  {panel.points.map((pt) => (
                    <li key={pt} className="tz-bullet-item">
                      <span className="tz-bullet" />
                      <span className="tz-bullet-text">{pt}</span>
                    </li>
                  ))}
                </ul>
                {panel.ctaType === 'primary' ? (
                  <button
                    onClick={() => navigate(ROUTES.REGISTER, { state: { role: panel.role } })}
                    className="btn tz-btn-primary-lg"
                  >
                    {panel.cta}
                  </button>
                ) : (
                  <a
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(ROUTES.REGISTER, { state: { role: panel.role } });
                    }}
                    className="tz-link"
                  >
                    {panel.cta} →
                  </a>
                )}
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
const BlueSection: React.FC = () => (
  <section className="tz-blue-section">
    <div className="container tz-container">
      <div className="row justify-content-center">
        <div className="col-lg-8 text-center">
          <p className="tz-blue-eyebrow">Why ToothZap</p>
          <h2 className="tz-blue-title">
            Faster workflow, fewer follow-ups, and same-day crown delivery.
          </h2>
          <p className="tz-blue-text">
            ToothZap connects clinics and milling partners so eligible crown cases can move from scan to fitment in one visit.
          </p>
        </div>
      </div>
    </div>
  </section>
);
const FAQSection: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section className="tz-section bg-white">
      <div className="container tz-faq-container py-5">
        <div className="py-4">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="tz-faq-title"
          >
            Questions clinics and patients ask before going same-day
          </motion.h2>

          {faqs.map((faq, i) => (
            <div key={i} className="tz-faq-item">
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-100 d-flex justify-content-between align-items-center bg-transparent border-0 text-start tz-faq-btn"
              >
                <span>{faq.q}</span>
                <span className="tz-faq-icon">{openIdx === i ? '−' : '+'}</span>
              </button>

              {openIdx === i && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="tz-faq-answer"
                >
                  {faq.a}
                </motion.p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTABand: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="tz-cta-band">
      <div className="container tz-container py-5">
        <div className="row align-items-center g-4 py-2">
          <div className="col-lg-8">
            <h2 className="tz-cta-title">Ready to offer same-day crowns?</h2>
            <p className="tz-cta-text">
              Join the network as a dentist, book a workflow demo, or explore how your lab can operate as a local production hub.
            </p>
          </div>
          <div className="col-lg-4">
            <div className="tz-cta-actions">
              <button
                onClick={() => navigate(ROUTES.REGISTER, { state: { role: 'Dentist' } })}
                className="tz-cta-btn tz-cta-btn-primary"
              >
                Join Us
              </button>

              <button
                onClick={() => navigate(ROUTES.REGISTER)}
                className="tz-cta-btn tz-cta-btn-outline"
              >
                Book a Demo
              </button>

              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTES.REGISTER, { state: { role: 'Milling Hub' } });
                }}
                className="tz-cta-link"
              >
                Partner as Hub
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};



const Home: React.FC = () => (
  <div style={{ overflowX: 'hidden', maxWidth: '100vw', width: '100%' }}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="tz-page"
    >
      <Nav />
      <Hero />
      <TrustBar />
      <ProblemSection />
      <HowItWorks />
      <AudiencePanels />
      <BlueSection />
      <FAQSection />
      <CTABand />
      <ToothZapFooter />
    </motion.div>
  </div>
);

export default Home;
